// Shrinking a picked image before it becomes a data URL.
//
// Every image in this system is stored as a base64 data URL in a text column and
// sent back inside ordinary JSON, so an image that is not shrunk on the way in is
// paid for on every single read afterwards. One profile photo went in untouched
// and became a 16.8 MB `avatarUrl`: /api/users returned sixteen megabytes for a
// single user, and that request sits in the batch the dashboard waits on before
// it will draw anything.
//
// The catalogue screen already did this correctly, with its own copy of the
// logic. This is that logic, in one place, so a new upload cannot quietly skip
// it — which is exactly how the avatar came to.
//
// Base64 also costs about a third on top of the raw bytes, so the saving here is
// larger than the pixel count suggests.
export const compressImage = (file, { maxWidth = 800, maxHeight = 800, quality = 0.8 } = {}) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onerror = () => reject(new Error('Could not read the file'));
    reader.onload = (e) => {
      const img = new Image();

      // A file the browser cannot decode — a PDF renamed to .png, say — is
      // handed back untouched rather than throwing the upload away. The caller
      // gets something to store, and the size guard below still applies.
      img.onerror = () => resolve(e.target.result);

      img.onload = () => {
        let { width, height } = img;

        if (width > height) {
          if (width > maxWidth) {
            height = Math.round(height * (maxWidth / width));
            width = maxWidth;
          }
        } else if (height > maxHeight) {
          width = Math.round(width * (maxHeight / height));
          height = maxHeight;
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);

        const compressed = canvas.toDataURL('image/webp', quality);

        // A small original that webp cannot beat is kept as it is: re-encoding a
        // tiny icon can come out larger than it went in.
        resolve(compressed.length < e.target.result.length ? compressed : e.target.result);
      };

      img.src = e.target.result;
    };

    reader.readAsDataURL(file);
  });
